import { createRef, FormEvent, MutableRefObject, RefObject, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm, useWatch } from "react-hook-form";
import { ICustomerDetails } from "@interface/customerDetails/ICustomerDetails";
import {
    cartRequiresSimDeliveryAddress,
    formatUserDate,
    getCustomerDetailsRequest,
    hasAtLeastOnePSimInCart,
    hasOnlyESimInCart,
} from "./utils/customerDetailsUtils";
import { CustomerDetailsPersonalDetails } from "@features/customerDetails/CustomerDetailsPersonalDetails";
import { CustomerDetailsBillingAddress } from "@features/customerDetails/CustomerDetailsBillingAddress";
import { CustomerDetailsDeliveryAddress } from "@features/customerDetails/CustomerDetailsDeliveryAddress";
import { CheckboxWrapper } from "@common/CheckboxWrapper/CheckboxWrapper";
import { Trans, useTranslation } from "react-i18next";
import { BRAND_NAME, EServiceGroup, WORDPRESS_URL } from "@constants";
import { MarketingPreferences } from "@features/customerDetails/marketingPreferences/MarketingPreferences";
import { mapMarketingPreferencesForm } from "@features/customerDetails/mappers/mapMarketingPreferencesForm";
import { ICustomerDetailsForm } from "@features/customerDetails/ICustomerDetailsForm";
import { keycloakUtils } from "itsf-ui-common";
import { ICart } from "@features/cart/ICart";
import { Box, Divider, Grid, Link, Typography } from "@mui/material";
import { FlowCard } from "@common/FlowCard/FlowCard";
import { hasOfferBroadband, selectOfferInCartByServiceGroup } from "@utils/offerUtils";
import { CustomerDetailsBroadbandAddress } from "@features/customerDetails/CustomerDetailsBroadbandAddress";
import { useExposeSubmit } from "@hooks/useExposeSubmit";
import { useStyle } from "@features/customerDetails/style";

interface IParams {
    customerDetails: ICustomerDetails;
    emailError?: string;
    pathName: string;
    setScrollToError(scrollToError: boolean): void;
    updateCustomerDetails(
        customerDetailsRequest: ICustomerDetails,
        privacyPolicyValue: boolean,
        recaptchaValue: string | undefined
    ): Promise<void>;
    cart: ICart;
    onSubmitForm?: MutableRefObject<(() => void) | null>;
}

export const CustomerDetailsForm = (props: IParams) => {
    const { updateCustomerDetails, setScrollToError, customerDetails, emailError, cart, onSubmitForm } = props;

    const { t } = useTranslation(["customerDetails", "common", "marketingPreferences"]);
    const { classes } = useStyle();
    const isAuthenticated = keycloakUtils.isAuthenticated();

    const hasBroadbandInCart = hasOfferBroadband(cart.offers);

    const getDefaultValue = (): ICustomerDetailsForm => {
        const customerPermissions = mapMarketingPreferencesForm(customerDetails.contactPermission);

        const deliveryAddressStringify = JSON.stringify(customerDetails.deliveryAddress);
        const billingAddressStringify = JSON.stringify(customerDetails.billingAddress);

        return {
            billingAddress: {
                addressLine1: customerDetails.billingAddress?.addressLine1 ?? "",
                addressLine2: customerDetails.billingAddress?.addressLine2,
                addressLine3: customerDetails.billingAddress?.addressLine3,
                code: customerDetails.billingAddress?.eircode ?? "",
                county: customerDetails.billingAddress?.county ?? "",
                town: customerDetails.billingAddress?.town ?? "",
            },
            deliveryAddress: {
                addressLine1: customerDetails.deliveryAddress?.addressLine1 ?? "",
                addressLine2: customerDetails.deliveryAddress?.addressLine2,
                addressLine3: customerDetails.deliveryAddress?.addressLine3,
                code: customerDetails.deliveryAddress?.eircode ?? "",
                county: customerDetails.deliveryAddress?.county ?? "",
                town: customerDetails.deliveryAddress?.town ?? "",
            },
            isSameDeliveryAddress: deliveryAddressStringify === billingAddressStringify || hasBroadbandInCart,
            contactPermissions: customerPermissions,
            birthDate: customerDetails.birthDate ? formatUserDate(customerDetails.birthDate) : "",
            email: customerDetails.email ?? "",
            emailConfirm: customerDetails.email ?? "",
            firstName: customerDetails.firstName ?? "",
            lastName: customerDetails.lastName ?? "",
            mobileNumber: customerDetails.mobileNumber ?? "",
            privacyPolicy: false,
        };
    };

    const formMethods = useForm<ICustomerDetailsForm>({
        defaultValues: getDefaultValue(),
    });

    const captcha: RefObject<ReCAPTCHA> = createRef();
    const formRef = useRef<HTMLFormElement>(null);

    useExposeSubmit(formRef, onSubmitForm);

    const isSameDeliveryAddress = useWatch<boolean>({ name: "isSameDeliveryAddress", control: formMethods.control });
    const isAtLeastOnePhysicalSimInCart = hasAtLeastOnePSimInCart(cart);
    const isAllESimInCart = hasOnlyESimInCart(cart);
    const requiresSimDeliveryAddress = cartRequiresSimDeliveryAddress(cart);
    const isSameAddressCheckboxDisplayed =
        (requiresSimDeliveryAddress && hasBroadbandInCart) ||
        (!isAuthenticated && isAtLeastOnePhysicalSimInCart && (!hasBroadbandInCart || requiresSimDeliveryAddress));
    const displayDeliveryAddress =
        (isSameAddressCheckboxDisplayed && !isSameDeliveryAddress) ||
        (isAuthenticated && !hasBroadbandInCart && isAtLeastOnePhysicalSimInCart);
    const showDeliveryAddressHeading = displayDeliveryAddress || (requiresSimDeliveryAddress && isSameDeliveryAddress);
    const broadbandInstallationAddress = selectOfferInCartByServiceGroup(cart, EServiceGroup.SA_BB)?.prequal
        ?.addressResponse;

    const showDeliveryAddressBlock =
        showDeliveryAddressHeading ?? displayDeliveryAddress ?? isSameAddressCheckboxDisplayed;
    const showDividerAfterBroadband = hasBroadbandInCart && showDeliveryAddressBlock;
    const showDividerAfterBilling = !isAuthenticated && !hasBroadbandInCart && showDeliveryAddressBlock;

    const executeRecaptcha = (recaptchaValue: string) => {
        updateDetails(recaptchaValue);
    };

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const captchaValue = captcha?.current ?? undefined;

        if (await formMethods.trigger()) {
            if (isAuthenticated) {
                updateDetails(undefined);
            } else if (captchaValue) {
                captchaValue.execute();
            }
        } else {
            const { errors } = formMethods.formState;
            const scrollToError = Boolean(errors.privacyPolicy) && Object.keys(errors).length === 1;

            if (scrollToError) {
                setScrollToError(true);
            }
        }
    };

    const updateDetails = (recaptchaValue?: string) => {
        const formData = formMethods.getValues();

        const customerDetailsRequest = getCustomerDetailsRequest(
            formData,
            displayDeliveryAddress,
            customerDetails,
            isAuthenticated,
            isAllESimInCart,
            broadbandInstallationAddress
        );

        updateCustomerDetails(customerDetailsRequest, formData.privacyPolicy, recaptchaValue).then(() => {
            if (captcha?.current) {
                captcha.current.reset();
            }
        });
    };

    const privacyPolicySentence = (
        <Trans
            components={[
                <Link
                    color="primary"
                    href={`${WORDPRESS_URL}/terms`}
                    key="policyLink"
                    rel="noopener"
                    target="_blank"
                    underline="always"
                />,
            ]}
            i18nKey="policy"
            ns="customerDetails"
            values={{ brand: BRAND_NAME }}
        />
    );

    const showAddressCard =
        hasBroadbandInCart ||
        (!isAuthenticated && !hasBroadbandInCart) ||
        displayDeliveryAddress ||
        isSameAddressCheckboxDisplayed;

    return (
        <>
            <form ref={formRef} onSubmit={onSubmit}>
                <Grid container direction="column" spacing={4}>
                    <Grid className={classes.card} item>
                        <FlowCard title={t("customerDetails:personalDetails")}>
                            <CustomerDetailsPersonalDetails
                                emailError={emailError}
                                formMethods={formMethods}
                                hasBroadbandInCart={hasBroadbandInCart}
                            />
                        </FlowCard>
                    </Grid>
                    {showAddressCard && (
                        <Grid className={classes.card} item>
                            <FlowCard title={t("customerDetails:myAddress")}>
                                {hasBroadbandInCart && (
                                    <>
                                        <CustomerDetailsBroadbandAddress
                                            installationAddress={broadbandInstallationAddress}
                                            isAuthenticated={isAuthenticated}
                                        />
                                        {showDividerAfterBroadband && <Divider classes={{ root: classes.divider }} />}
                                    </>
                                )}
                                {!isAuthenticated && !hasBroadbandInCart && (
                                    <>
                                        <CustomerDetailsBillingAddress formMethods={formMethods} />
                                        {showDividerAfterBilling && <Divider classes={{ root: classes.divider }} />}
                                    </>
                                )}
                                {showDeliveryAddressHeading && (
                                    <Typography color="secondary" variant="h4">
                                        {t("customerDetails:deliveryAddress")}
                                    </Typography>
                                )}
                                {displayDeliveryAddress && <CustomerDetailsDeliveryAddress formMethods={formMethods} />}
                                {isSameAddressCheckboxDisplayed && (
                                    <Box marginTop={1.5}>
                                        <CheckboxWrapper
                                            color="primary"
                                            control={formMethods.control}
                                            fullWidth
                                            id="sameBillingAddress"
                                            isRequired={false}
                                            label={
                                                hasBroadbandInCart
                                                    ? t("customerDetails:sameInstallationAddress")
                                                    : t("customerDetails:sameAddress")
                                            }
                                            name="isSameDeliveryAddress"
                                        />
                                    </Box>
                                )}
                            </FlowCard>
                        </Grid>
                    )}
                    {!isAuthenticated && (
                        <Grid className={classes.card} item>
                            <FlowCard title={t("marketingPreferences:title")}>
                                <MarketingPreferences formMethods={formMethods} />
                            </FlowCard>
                        </Grid>
                    )}
                    <Grid className={classes.card} item>
                        <CheckboxWrapper
                            color="primary"
                            control={formMethods.control}
                            error={formMethods.errors.privacyPolicy}
                            fullWidth
                            id="policy"
                            isRequired
                            label={privacyPolicySentence}
                            name="privacyPolicy"
                        />
                    </Grid>
                </Grid>
            </form>
            <ReCAPTCHA
                ref={captcha}
                sitekey={process.env.RECAPTCHA_SITE_KEY ? process.env.RECAPTCHA_SITE_KEY : ""}
                size="invisible"
                onChange={executeRecaptcha}
            />
        </>
    );
};
